import React, { useState, useEffect, useCallback } from 'react'
import Dropzone from 'react-dropzone'
import className from 'classnames'
import { Modal, Tabs, Icon, Button, Select, message } from 'antd'
import axios from 'axios'
import {
  bytesToSize,
  getFileType,
  getFileExtension
} from '../../../../../utils'
import AddMediaDetail from '../../../components/AddMediaDetail/'
import ARUploads from '../../../components/ARUploads'
import MazeUploads from '../../../components/MazeUploads/'
import {
  FaFilePdf,
  FaFileWord,
  FaFilePowerpoint,
  FaFileExcel,
  FaFileAlt,
  FaProjectDiagram,
  FaPlay,
  FaVolumeUp
} from 'react-icons/fa'
import uuidv4 from 'uuid/v4'

import './UploadModal.scss'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { general, config } from '../../../../../constants'
import { api } from '../../../../../services'

const { Option } = Select

const { TabPane } = Tabs

const UploadModal = ({
  visible,
  user,
  isLibrary = false,
  onCancel,
  refreshData,
  topics,
  library,
  upload,
  clearUploadList,
  editAssets,
  selectedData,
  tab,
  parent_id,
  isKnowledgeCenter,
  documentTypes,
  shlToken
}) => {
  const [files, setFiles] = useState([])
  const [selectedDocTypes, setSelectedDocTypes] = useState({})
  const [isUploadingDeferred, setIsUploadingDeferred] = useState(false)
  const [fileNotCompatible, setFileNotCompatible] = useState(false)
  const [enableProgress, setEnableProgress] = useState(false)
  const [isRemovingMedia, setIsRemovingMedia] = useState(false)
  const [scormExtractedMedia, setScormExtractedMedia] = useState([])
  const [distroyUnity, setDistroyUnity] = useState(false)
  const [activeTabKey, setActiveTabKey] = useState('1')

  const { t } = useTranslation()

  useEffect(() => {
    if (!visible) {
      setFiles([])
      setSelectedDocTypes({})
      setFileNotCompatible(false)
      setEnableProgress(false)
      setIsUploadingDeferred(false)
    }
  }, [visible])

  const isDocumentType = (file) => {
    if (!file) return false
    const fileExtension = (file.name || '').split('.').pop().toLowerCase()
    const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'xls', 'xlsx', 'ppt', 'pptx']
    return documentExtensions.includes(fileExtension)
  }

  const onDrop = useCallback(
    (dropped, isScormZipUpload) => {
      // Add unique ID to each dropped file
      const droppedWithIds = dropped.map(file => ({
        file,
        uid: uuidv4(),
        removed: false
      }))

      const newFiles = files.concat(droppedWithIds)
      setFiles(newFiles)

      if (dropped.length === 0) {
        setFileNotCompatible(true)
        return
      }

      // If documentTypes are provided and it's not SCORM, check if any file is a document
      if (documentTypes && !isScormZipUpload) {
        const hasDocument = droppedWithIds.some(f => isDocumentType(f.file))
        if (hasDocument) {
          return
        }
      }

      setEnableProgress(true)

      droppedWithIds.forEach(fileObj => {
        upload(
          fileObj.file,
          isScormZipUpload
            ? { category: 'scorm', parent_id: parent_id }
            : { parent_id: parent_id },
          tab,
          fileObj.uid
        ).then(res => {
          setFiles(prevFiles =>
            prevFiles.map(file => {
              if (file.uid === fileObj.uid) {
                return {
                  ...file,
                  mediaId: res.id
                }
              }
              return file
            })
          )
          if (res && isScormZipUpload) {
            setScormExtractedMedia(res)
            refreshData()
          }
        })
      })
    },
    [files, upload, parent_id, tab, documentTypes]
  )

  const handleStartUpload = async () => {
    setEnableProgress(true)
    setIsUploadingDeferred(true)

    const uploadPromises = files.map(async (fileObj) => {
      if (fileObj.removed) return

      try {
        const res = await upload(
          fileObj.file,
          { parent_id: parent_id },
          tab,
          fileObj.uid
        )

        if (res && res.id) {
          // Update file with mediaId
          setFiles(prevFiles =>
            prevFiles.map(file => {
              if (file.uid === fileObj.uid) {
                return {
                  ...file,
                  mediaId: res.id
                }
              }
              return file
            })
          )

          // Update document type if selected and file is a document
          if (isDocumentType(fileObj.file)) {
            const docType = selectedDocTypes[fileObj.uid]
            if (docType) {
              const documentTypeMap = {
                'global_sop_keystroke': 1,
                'bp_specific_sop_keystroke': 2,
                'global_statement_of_work': 3,
                'appendix_document': 4,
                'general_document': 5
              }

              const document_type_id = documentTypeMap[docType]

              if (document_type_id) {
                try {
                  const apiResponse = await axios.put(
                    `${config.aiContentGenerationUrl}sop-management/media/${res.id}/document-type?org_id=${res.organization_id}`,
                    { document_type_id, title: fileObj.file.name },
                    { headers: { Authorization: `Bearer ${shlToken}`, "Content-Type": "application/json" } }
                  )
                  console.log("✅ Updated document type successfully:", apiResponse.data)
                } catch (error) {
                  console.error("❌ Document type update failed:", error.response?.data || error.message)
                  message.warning(`Document uploaded but failed to update type for ${fileObj.file.name}`)
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Upload failed for file:", fileObj.file.name, error)
      }
    })

    await Promise.all(uploadPromises)

    setIsUploadingDeferred(false)
    refreshData()
  }

  const handleCancelUpload = useCallback(mediaItem => {
    if (mediaItem && mediaItem.source) {
      mediaItem.source.cancel()
    }
  }, [])

  const handleRemoveMedia = useCallback(async (mediaId, fileId) => {
    setIsRemovingMedia(true)
    const data = {
      mediaIds: [mediaId]
    }
    try {
      await api.library.removeMedias(data)
      refreshData()

      // Update the specific file by ID instead of index
      setFiles(prevFiles =>
        prevFiles.map(fileObj =>
          fileObj.id === fileId ? { ...fileObj, removed: true } : fileObj
        )
      )

      setIsRemovingMedia(false)
    } catch (error) {
      console.error('Error removing media:', error)
      setIsRemovingMedia(false)
    }
  }, [])

  const getThumbnail = (file, isScorm = false) => {
    if (!file) return null

    const fileType = getFileType(file.type)
    const fileExtension = (file.name || '')
      .split('.')
      .pop()
      .toLowerCase()

    let IconComponent = FaFileAlt
    let iconColor = '#607D8B'

    // Handle audio files
    if (
      fileType === 'audio' ||
      ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(fileExtension)
    ) {
      IconComponent = FaVolumeUp
      iconColor = '#5B6EE2'
      return (
        <div className="upload-modal__thumbnail__audio-video">
          <IconComponent style={{ fontSize: 48, color: iconColor }} />
        </div>
      )
    }

    // Handle video files
    if (
      fileType === 'video' ||
      ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'].includes(
        fileExtension
      )
    ) {
      IconComponent = FaPlay
      iconColor = '#5B6EE2'
      return (
        <div className="upload-modal__thumbnail__audio-video">
          <IconComponent style={{ fontSize: 48, color: iconColor }} />
        </div>
      )
    }

    if (
      fileType === 'application' ||
      [
        'pdf',
        'doc',
        'docx',
        'ppt',
        'pptx',
        'xls',
        'xlsx',
        'drawio',
        'vsdx'
      ].includes(fileExtension)
    ) {
      if (file.type === 'application/pdf' || fileExtension === 'pdf') {
        IconComponent = FaFilePdf
        iconColor = '#FF4444'
      } else if (
        file.type === 'application/msword' ||
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        ['doc', 'docx'].includes(fileExtension)
      ) {
        IconComponent = FaFileWord
        iconColor = '#4A90E2'
      } else if (
        file.type === 'application/vnd.ms-powerpoint' ||
        file.type ===
        'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        ['ppt', 'pptx'].includes(fileExtension)
      ) {
        IconComponent = FaFilePowerpoint
        iconColor = '#D24726'
      } else if (
        file.type === 'application/vnd.ms-excel' ||
        file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        ['xls', 'xlsx'].includes(fileExtension)
      ) {
        IconComponent = FaFileExcel
        iconColor = '#217346'
      } else if (['drawio', 'vsdx'].includes(fileExtension)) {
        IconComponent = FaProjectDiagram
        iconColor = '#607D8B'
      }
      return (
        <div className="upload-modal__thumbnail__audio-video">
          <IconComponent style={{ fontSize: 48, color: iconColor }} />
        </div>
      )
    }

    if (fileType === 'text') {
      return <FaFileAlt style={{ fontSize: 48, color: '#607D8B' }} />
    }

    if (isScorm) {
      return (
        <div className="upload-modal__thumbnail__audio-video">
          <FaFileAlt style={{ fontSize: 48, color: '#607D8B' }} />
        </div>
      )
    }

    if (fileType === 'image') {
      return (
        <img
          className="upload-modal__thumbnail__image"
          src={URL.createObjectURL(file)}
          alt=""
        />
      )
    }

    return (
      <div className="upload-modal__thumbnail__audio-video">
        <FaFileAlt style={{ fontSize: 48, color: '#607D8B' }} />
      </div>
    )
  }

  const checkedCompletUpload = item => {
    const uploadSuccess = library.uploadSuccess || []
    const filterSuccessFiles = uploadSuccess.filter(ele => ele.id === item.id)
    return !(uploadSuccess.length > 0 && filterSuccessFiles.length > 0)
  }

  const cancelError = useCallback(() => {
    setFileNotCompatible(false)
  }, [])

  const onChangeUploadsTab = useCallback(
    key => {
      setActiveTabKey(key)
      setEnableProgress(false)
      // setFiles([])
      setScormExtractedMedia([])
      clearUploadList()
    },
    [clearUploadList]
  )

  const enableCloseModal = (progress, isFetching) => {
    const filterCancelProgressUpload = _.filter(
      progress,
      item => item.status === general.CANCELED
    )
    const checkCancelUpload =
      filterCancelProgressUpload.length === progress.length

    if (isFetching) {
      return !checkCancelUpload
    } else if (isRemovingMedia) {
      return true
    }

    return false
  }

  const renderDropzone = type => {
    const isScormZipUpload = type === 'scorm'
    const droppableFiles = [
      'video/AVI',
      'video/WMV',
      'video/MP4',
      'video/MOV',
      'video/FLV',
      'video/*',
      'audio/*',
      '.pdf',
      'image/*',
      '.ppt',
      '.pptx',
      '.txt',
      '.docx',
      '.xls',
      '.xlsx',
      '.drawio',
      '.vsdx'
    ]

    const uploadProgress = library.uploadProgress || []
    const uploadFetching = library.uploadFetching || false

    return (
      <div className="upload-modal">
        <div className="upload-modal__inner">
          {!enableProgress && files.length === 0 && (
            <div>
              <h3 className="upload-modal__text">
                {isScormZipUpload
                  ? t('v4:upload_scorm_zip')
                  : t('media:import_file')}
              </h3>
              <div className="upload-modal__dropzone">
                <Dropzone
                  accept={
                    isScormZipUpload
                      ? ['application/zip', 'application/x-zip-compressed']
                      : droppableFiles
                  }
                  onDrop={dropped => onDrop(dropped, isScormZipUpload)}
                >
                  {({ isDragActive }) => (
                    <div
                      className={className('upload-dropzone', {
                        'upload-dropzone_active': isDragActive
                      })}
                    >
                      <div className="upload-dropzone__text-wrapper">
                        <p className="upload-dropzone__text">
                          <span>
                            {isScormZipUpload
                              ? t('v2:upload_3d_maze_zip')
                              : t('media:upload_file')}
                          </span>{' '}
                          {t('media:drag_drop')}
                        </p>
                      </div>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          )}

          {!enableProgress && files.length > 0 && documentTypes && (
            <div className="upload-modal__file-list">
              <h3 className="upload-modal__text">Select Document Types</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                {files.map((fileObj, index) => (
                  <div key={fileObj.uid} className="upload-modal__file-item" style={{ display: 'flex', alignItems: 'center', padding: '10px', borderBottom: '1px solid #eee' }}>
                    <div style={{ marginRight: '15px' }}>
                      {getThumbnail(fileObj.file, type === 'scorm')}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{fileObj.file.name}</div>
                      <div style={{ color: '#888', fontSize: '12px' }}>{bytesToSize(fileObj.file.size)}</div>
                    </div>
                    <div style={{ width: '250px' }}>
                      {isDocumentType(fileObj.file) ? (
                        <Select
                          placeholder="Select Document Type"
                          style={{ width: '100%' }}
                          value={selectedDocTypes[fileObj.uid]}
                          onChange={(value) => {
                            setSelectedDocTypes(prev => ({
                              ...prev,
                              [fileObj.uid]: value
                            }))
                          }}
                        >
                          {documentTypes.map(type => (
                            <Option key={type.value} value={type.value}>{type.label}</Option>
                          ))}
                        </Select>
                      ) : (
                        <div style={{ color: '#888', fontStyle: 'italic' }}>Not required</div>
                      )}
                    </div>
                    <Button
                      type="link"
                      icon="delete"
                      onClick={() => {
                        setFiles(prev => prev.filter(f => f.uid !== fileObj.uid))
                        setSelectedDocTypes(prev => {
                          const newState = { ...prev }
                          delete newState[fileObj.uid]
                          return newState
                        })
                      }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'right', marginTop: '20px' }}>
                <Button onClick={() => {
                  setFiles([])
                  setSelectedDocTypes({})
                }} style={{ marginRight: '10px' }}>
                  Clear All
                </Button>
                <Button
                  type="primary"
                  onClick={handleStartUpload}
                  disabled={files.some(f => isDocumentType(f.file) && !selectedDocTypes[f.uid])}
                >
                  Start Upload
                </Button>
              </div>
            </div>
          )}
          <div className="upload-modal__list-container">
            {fileNotCompatible && (
              <div className="upload-modal__error-item-wrapper">
                <div className="upload-modal__error-message-container">
                  <div className="upload-modal__error-bar" />
                  <div className="upload-modal__error-message">
                    {t('errors:upload_error')}
                  </div>
                </div>
                <Button
                  className="upload-modal__cancel-error"
                  type="primary"
                  onClick={cancelError}
                >
                  {t('buttons:cancel')}
                </Button>
              </div>
            )}

            {enableProgress && uploadProgress.length > 0 && (
              <div className="upload-modal__list">
                {uploadProgress.map((item, ind) => {
                  // Find the corresponding file by matching upload progress index
                  // This assumes uploadProgress order matches the order files were uploaded
                  const fileObj =
                    files.find(file => file.uid === item.uid) || files[ind]
                  const file = fileObj?.file

                  console.log('check file', {
                    files,
                    item,
                    ind
                  })

                  return (
                    <div key={item.uid} className="upload-modal__item">
                      <div className="upload-modal__thumbnail">
                        {getThumbnail(file, type === 'scorm')}
                        <div className="upload-modal__thumbnail__details">
                          <span style={{ color: '#607D8B' }}>
                            {file && file.name}
                          </span>
                          <span className="upload-modal__thumbnail__media-type">
                            {file && getFileExtension(file.type)}
                          </span>
                          {/* <span
                            className="upload-modal__thumbnail__extention"
                            title={file && getFileExtension(file.type)}
                          >
                          </span> */}
                          <span className="upload-modal__thumbnail__size">
                            {file && bytesToSize(file.size)}
                          </span>
                        </div>
                      </div>
                      <div className="upload-modal-item">
                        <div className="upload-modal-item__percent-border">
                          <div
                            className={`upload-modal-item__percent ${item.status === 'CANCELED'
                              ? 'upload-modal-item__percent__cancel'
                              : ''
                              }`}
                            style={{ width: item.percent + '%' }}
                          />
                        </div>

                        {item.status !== 'CANCELED' && (
                          <div className="upload-modal-item__weight">
                            {checkedCompletUpload(item) && (
                              <span>
                                {bytesToSize(item.loaded)} {t('of')}{' '}
                                {bytesToSize(item.total)}
                              </span>
                            )}
                            {!checkedCompletUpload(item) && (
                              <span className="upload-modal-item__weight__complete">
                                {fileObj && fileObj.removed
                                  ? t('general:removed')
                                  : `${t('general:completed')} ${type === 'scorm'
                                    ? `(${scormExtractedMedia.length} ${t(
                                      'media:files_extracted'
                                    )})`
                                    : ''
                                  }`}
                              </span>
                            )}
                          </div>
                        )}

                        {item.status === 'CANCELED' && (
                          <div className="upload-modal-item__weight">
                            <span className="upload-modal-item__weight__canceled">
                              {t('general:canceled')}
                            </span>
                          </div>
                        )}

                        <AddMediaDetail
                          user={user}
                          status={item.status}
                          mediaId={fileObj.mediaId}
                          fileType={file && getFileType(file.type)}
                          isLibrary={isLibrary}
                          topicList={topics.list}
                          isLoadingTopic={topics.isFetching}
                          isRemovingMedia={isRemovingMedia}
                          mediaRemoved={fileObj && fileObj.removed}
                          ctrlHandleCancel={() => handleCancelUpload(item)}
                          ctrlHandleRemove={() =>
                            handleRemoveMedia(item.id, fileObj?.id)
                          }
                          refreshData={refreshData}
                          isScorm={type === 'scorm'}
                          scormExtractedMedia={scormExtractedMedia}
                          isUploading={uploadFetching}
                          isUploadingComplete={checkedCompletUpload(item)}
                          uploadProgressItem={item}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <div className="upload-modal__footer">
          <Button
            type="primary"
            shape="round"
            size="large"
            onClick={() => {
              onCancel()
              onChangeUploadsTab('1')
            }}
            disabled={enableCloseModal(
              library.uploadProgress,
              library.uploadFetching
            )}
          >
            {t('buttons:close')}
          </Button>
        </div>
      </div>
    )
  }

  console.log({ uploadProgress: library.uploadProgress, files })

  return (
    <Modal
      className="custom-ant-modal upload-modal-container"
      destroyOnClose
      width={1200}
      footer={null}
      visible={visible}
      onCancel={() => {
        onCancel()
        onChangeUploadsTab('1')
      }}
      maskClosable={false}
      centered
    >
      <Tabs
        activeKey={activeTabKey}
        onChange={onChangeUploadsTab}
        animated={false}
      >
        {!editAssets && (
          <TabPane tab={t('v2:upload_assets')} key="1">
            {renderDropzone('media')}
          </TabPane>
        )}
        {!isKnowledgeCenter && (
          <TabPane
            tab={editAssets ? t('v2:edit_ar_assets') : t('v2:upload_ar_assets')}
            key="2"
          >
            <ARUploads
              tab={tab}
              selectedData={selectedData}
              editAssets={editAssets}
              distroyUnity={distroyUnity}
              hideModal={onCancel}
            />
          </TabPane>
        )}
        {!isKnowledgeCenter && (
          <TabPane
            tab={
              editAssets ? t('v2:edit_maze_assets') : t('v2:upload_maze_assets')
            }
            key="3"
          >
            <MazeUploads
              tab={tab}
              selectedData={selectedData}
              editAssets={editAssets}
              hideModal={onCancel}
            />
          </TabPane>
        )}

        {!isKnowledgeCenter && (
          <TabPane tab={t('v4:upload_scorm_zip')} key="4">
            {renderDropzone('scorm')}
          </TabPane>
        )}
      </Tabs>
    </Modal>
  )
}

export default UploadModal
